import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Scales, MagnifyingGlass, X } from '@phosphor-icons/react';
import { Input } from '../components/ui/input';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { BACKEND_URL } from '../config';

const CHART_COLORS = ['hsl(223, 100%, 33%)', 'hsl(9, 100%, 50%)', 'hsl(46, 100%, 50%)', 'hsl(160, 60%, 45%)', 'hsl(280, 60%, 50%)'];

const ClientComparison = ({ clients }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const toggleClient = (id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 5) { toast.error('Maximum 5 clients for comparison'); return prev; }
      return [...prev, id];
    });
  };

  const handleCompare = useCallback(async () => {
    if (selectedIds.length < 2) { toast.error('Select at least 2 clients'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/clients/compare`, { client_ids: selectedIds }, { withCredentials: true });
      setComparison(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Comparison failed');
    } finally {
      setLoading(false);
    }
  }, [selectedIds]);

  const clearComparison = () => {
    setComparison(null);
    setSelectedIds([]);
  };

  const filteredClients = searchQuery.trim()
    ? clients.filter(c => c.name?.toLowerCase().includes(searchQuery.toLowerCase()) || c.company?.toLowerCase().includes(searchQuery.toLowerCase()))
    : clients;

  // Build chart data
  const chartData = comparison
    ? comparison.months.map((month) => {
        const point = { month };
        comparison.clients.forEach((c) => {
          const md = c.monthly_data.find(d => d.month === month);
          point[c.name] = md ? md.revenue : 0;
        });
        return point;
      })
    : [];

  const formatMonth = (label) => {
    if (!label) return '';
    const [year, month] = label.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(month) - 1]} '${year.slice(2)}`;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-4">
        <Scales size={24} weight="regular" />
        <div>
          <div className="text-xl font-semibold tracking-tight">Client Comparison</div>
          <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground">SIDE-BY-SIDE ANALYSIS</div>
        </div>
      </div>

      {!comparison ? (
        <div className="bg-card border border-border rounded-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="relative flex-1 max-w-xs">
              <MagnifyingGlass size={16} weight="regular" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search clients..." className="pl-9 rounded-sm" data-testid="compare-search-input" />
            </div>
            <Button onClick={handleCompare} disabled={selectedIds.length < 2 || loading} className="rounded-sm" data-testid="compare-btn">
              {loading ? 'Comparing...' : `Compare (${selectedIds.length})`}
            </Button>
          </div>

          <div className="max-h-60 overflow-y-auto border border-border rounded-sm divide-y divide-border">
            {filteredClients.slice(0, 50).map((client) => (
              <label
                key={client.id}
                data-testid={`compare-client-${client.id}`}
                className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-muted/50 transition-colors ${selectedIds.includes(client.id) ? 'bg-accent/5' : ''}`}
              >
                <Checkbox checked={selectedIds.includes(client.id)} onCheckedChange={() => toggleClient(client.id)} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{client.name}</div>
                  {client.company && <div className="text-xs text-muted-foreground">{client.company}</div>}
                </div>
                <span className="text-xs text-muted-foreground shrink-0">₹{client.total_sales?.toLocaleString()}</span>
              </label>
            ))}
          </div>
          {selectedIds.length > 0 && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">Selected:</span>
              {selectedIds.map(id => {
                const c = clients.find(cl => cl.id === id);
                return (
                  <span key={id} className="inline-flex items-center gap-1 bg-muted px-2 py-0.5 rounded-sm text-xs">
                    {c?.name}
                    <button onClick={() => toggleClient(id)} className="ml-0.5 hover:text-destructive"><X size={10} weight="bold" /></button>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Comparing {comparison.clients.length} clients</span>
            <Button variant="outline" size="sm" className="rounded-sm" onClick={clearComparison} data-testid="clear-comparison-btn">
              <X size={14} weight="bold" className="mr-1" /> Clear
            </Button>
          </div>

          {/* Chart */}
          <div className="bg-card border border-border rounded-sm p-5">
            <div className="text-sm font-semibold mb-4">Monthly Revenue Comparison</div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tickFormatter={formatMonth} stroke="hsl(var(--foreground))" style={{ fontSize: '11px' }} />
                <YAxis stroke="hsl(var(--foreground))" style={{ fontSize: '11px' }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '0.25rem', fontSize: '12px' }} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                {comparison.clients.map((c, i) => (
                  <Line key={c.id} type="monotone" dataKey={c.name} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Summary table */}
          <div className="bg-card border border-border rounded-sm overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Client</th>
                  <th className="text-right py-3 px-4 text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Total Revenue</th>
                  <th className="text-right py-3 px-4 text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Avg Monthly</th>
                  <th className="text-center py-3 px-4 text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Active Months</th>
                </tr>
              </thead>
              <tbody>
                {comparison.clients.map((c, i) => (
                  <tr key={c.id} className="border-b border-border" data-testid={`comparison-row-${c.id}`}>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                        <div>
                          <div className="font-medium text-sm">{c.name}</div>
                          {c.company && <div className="text-xs text-muted-foreground">{c.company}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right font-semibold text-sm">₹{c.total_sales.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right text-sm text-muted-foreground">₹{c.avg_monthly_revenue.toLocaleString()}</td>
                    <td className="py-3 px-4 text-center text-sm">{c.active_months}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientComparison;
